import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight } from '../components/Icons';
import { projects } from '../data/projects';
import useSwipe from '../hooks/useSwipe';
import { schemas, getProjectMeta } from '../config/seo';
import ResponsiveImage from '../components/ResponsiveImage';

const ProjectPage = () => {
    const { slug } = useParams();
    const [imageIndex, setImageIndex] = useState(0);

    // Find project by slug/id
    const project = projects.find(p => p.id === slug);
    const projectIndex = projects.findIndex(p => p.id === slug);

    // Get prev/next projects
    const prevProject = projectIndex > 0 ? projects[projectIndex - 1] : null;
    const nextProject = projectIndex < projects.length - 1 ? projects[projectIndex + 1] : null;

    // Swipe handlers
    const nextImage = () => {
        if (project?.images?.length > 1) {
            setImageIndex((prev) => (prev + 1) % project.images.length);
        }
    };

    const prevImage = () => {
        if (project?.images?.length > 1) {
            setImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
        }
    };

    // Swipe handlers - pass functions directly (not as object)
    const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe(nextImage, prevImage);

    // Reset image index when project changes
    useEffect(() => {
        setImageIndex(0);
        window.scrollTo(0, 0);
    }, [slug]);

    // Redirect if project not found
    if (!project) {
        return (
            <div className="project-page">
                <div className="container section-padding">
                    <h1>Project Not Found</h1>
                    <p>The project you're looking for doesn't exist.</p>
                    <Link to="/#portfolio" className="btn-primary-custom">
                        View All Projects
                    </Link>
                </div>
            </div>
        );
    }

    const meta = getProjectMeta(project, slug);

    return (
        <>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                <link rel="canonical" href={meta.url} />

                <meta property="og:title" content={meta.title} />
                <meta property="og:description" content={meta.description} />
                <meta property="og:image" content={meta.image} />
                <meta property="og:url" content={meta.url} />
                <meta property="og:type" content={meta.type} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={meta.title} />
                <meta name="twitter:description" content={meta.description} />
                <meta name="twitter:image" content={meta.image} />

                <script type="application/ld+json">
                    {JSON.stringify(schemas.project(project))}
                </script>
            </Helmet>

            <div className="project-page">
                {/* Hero Section */}
                <section className="project-page-hero">
                    <div
                        className="page-gallery"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <div
                            className="page-gallery-track"
                            style={{ transform: `translateX(-${imageIndex * 100}%)` }}
                        >
                            {project.images.map((img, idx) => (
                                <div className="page-gallery-slide" key={idx}>
                                    <ResponsiveImage
                                        src={img}
                                        alt={`${project.title} - ${project.category} - Image ${idx + 1}`}
                                        loading={idx === 0 ? "eager" : "lazy"}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="page-gallery-overlay"></div>

                        {/* Title Overlay - Center Top */}
                        <div className="page-hero-title-overlay">
                            <div className="container">
                                <h1 className="page-hero-title">{project.title}</h1>
                            </div>
                        </div>

                        {/* Gallery Navigation Arrows */}
                        {project.images.length > 1 && (
                            <>
                                <button
                                    className="page-gallery-nav prev"
                                    onClick={prevImage}
                                    aria-label="Previous image"
                                >
                                    <ChevronLeft />
                                </button>
                                <button
                                    className="page-gallery-nav next"
                                    onClick={nextImage}
                                    aria-label="Next image"
                                >
                                    <ChevronRight />
                                </button>
                                <div className="page-gallery-dots">
                                    {project.images.map((_, idx) => (
                                        <button
                                            key={idx}
                                            className={`page-gallery-dot ${idx === imageIndex ? 'active' : ''}`}
                                            onClick={() => setImageIndex(idx)}
                                            aria-label={`Go to image ${idx + 1}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Info Bar - Under Gallery */}
                    <div className="page-info-bar">
                        <div className="container">
                            <span className="page-category">{project.category}</span>
                            <Link to="/#portfolio" className="page-nav-link">
                                All Projects <ChevronRight />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Meta Information */}
                <section className="project-page-meta">
                    <div className="container">
                        <div className="row">
                            <div className="col-4 col-md-3">
                                <div className="project-page-meta-item">
                                    <span className="project-page-meta-label">Location</span>
                                    <span className="project-page-meta-value">{project.location}</span>
                                </div>
                            </div>
                            <div className="col-4 col-md-3">
                                <div className="project-page-meta-item">
                                    <span className="project-page-meta-label">Year</span>
                                    <span className="project-page-meta-value">{project.year}</span>
                                </div>
                            </div>
                            <div className="col-4 col-md-3">
                                <div className="project-page-meta-item">
                                    <span className="project-page-meta-label">Duration</span>
                                    <span className="project-page-meta-value">{project.duration}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="project-page-content section-padding">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-7">
                                <h2 className="page-section-title">Project Overview</h2>
                                <div className="divider"></div>
                                {Array.isArray(project.fullDescription) ?
                                    project.fullDescription.map((paragraph, idx) => (
                                        <p key={idx} className="page-text">{paragraph}</p>
                                    ))
                                    :
                                    <p className="page-text">{project.fullDescription}</p>
                                }
                            </div>
                            <div className="col-lg-4 offset-lg-1">
                                <div className="page-sidebar">
                                    <h3 className="page-sidebar-title">Services Provided</h3>
                                    <ul className="page-sidebar-list">
                                        {project.services.map((service, idx) => (
                                            <li key={idx}>{service}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="page-cta section-padding">
                    <div className="container text-center">
                        <h2 className="page-cta-title">Interested in a Similar Restoration?</h2>
                        <p className="page-cta-text">
                            Every building has a story worth preserving. Let's discuss how we can help with yours.
                        </p>
                        <Link to="/#contact" className="btn-primary-custom">
                            Start a Conversation
                        </Link>
                    </div>
                </section>

                {/* Navigation to Other Projects */}
                <section className="page-bottom-nav">
                    <div className="container">
                        <div className="row">
                            <div className="col-6">
                                {prevProject && (
                                    <Link to={`/portfolio/${prevProject.id}`} className="page-bottom-nav-link prev">
                                        <span className="page-bottom-nav-label">
                                            <ChevronLeft /> Previous Project
                                        </span>
                                        <span className="page-bottom-nav-title">{prevProject.title}</span>
                                    </Link>
                                )}
                            </div>
                            <div className="col-6 text-end">
                                {nextProject && (
                                    <Link to={`/portfolio/${nextProject.id}`} className="page-bottom-nav-link next">
                                        <span className="page-bottom-nav-label">
                                            Next Project <ChevronRight />
                                        </span>
                                        <span className="page-bottom-nav-title">{nextProject.title}</span>
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default ProjectPage;
